import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { CSSProperties, FC } from "react";
import styled from "styled-components";
import { LegacyImage, Markdown } from "components";
import { useCoordinates } from "hooks";
import { getImageFormat } from "lib/utils";
import { Introduction1TitleContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Introduction1TitleContent;
}

const TitleWithBackground: FC<Props> = withErrorBoundary(
  ({ className = "", content: { title, textBackground, description } }) => {
    const imageCoordinates = useCoordinates({
      containerSelector: ".TitleWithBackground__titleContainer",
      elSelector: ".introImage",
    });

    return (
      <StyledTitleWithBackground
        className={className}
        style={
          {
            "--intro-image-x-position": `${imageCoordinates.x}px`,
            "--intro-image-y-position": `${imageCoordinates.y}px`,
          } as CSSProperties
        }
      >
        <div className="TitleWithBackground Content">
          <div className="TitleWithBackground__titleContainer">
            <Markdown className="TitleWithBackground__titleText Font_40_48_600 lg:Font_32_40_600">{title}</Markdown>

            {textBackground.data && (
              <LegacyImage
                {...textBackground.data.textBackground}
                layout="fill"
                transform={{
                  format: getImageFormat(textBackground.data.textBackground.ext),
                }}
                className="TitleWithBackground__titleImage"
              />
            )}
          </div>

          <Markdown className="TitleWithBackground__description Color_secondary Font_24_32_400 md:Font_18_26_450">
            {description}
          </Markdown>
        </div>
      </StyledTitleWithBackground>
    );
  },
  {}
);

const StyledTitleWithBackground = styled.section`
  /* stylelint-disable color-function-notation */

  background: url("/images/noise.png"),
    radial-gradient(
      69.09% 115.97% at 100% 115.97%,
      rgba(126, 100, 251, 75%) 14.44%,
      rgba(255, 205, 133, 75%) 59.75%,
      rgba(246, 243, 241, 0%) 100%
    ),
    #faf7f4;

  /* stylelint-enable color-function-notation */

  background-blend-mode: overlay, normal, normal;
  background-repeat: no-repeat;

  /*
   * background-blend-mode doesn't work in safari when background-repeat: repeat;
   * so, it is necessary to stretch the image to the full width of the block,
   * when the block becomes larger than the width of the image
  */

  /* stylelint-disable-next-line media-feature-range-notation */
  @media (min-width: 1200px) {
    background-size: 100% 100%;
  }

  .TitleWithBackground {
    display: flex;
    justify-content: space-between;
    padding-top: 120px;
    padding-bottom: 546px;
  }

  .TitleWithBackground__titleContainer {
    position: relative;
    max-width: 582px;
    min-width: 506px;
    padding-left: 56px;
  }

  .TitleWithBackground__titleText {
    position: relative;
    z-index: 1;

    span {
      white-space: nowrap;
    }
  }

  .TitleWithBackground__titleImage {
    position: absolute;
    width: 301px;
    height: 73px;
    top: calc(var(--intro-image-y-position) - 6px);
    left: calc(var(--intro-image-x-position) + 102px);
    transform: translate(-10%);
    z-index: 0;
  }

  .TitleWithBackground__description {
    max-width: 476px;
    margin-left: 40px;
    padding-right: 56px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .TitleWithBackground {
      flex-direction: column;
      padding-top: 96px;
      padding-bottom: 80px;
    }

    .TitleWithBackground__titleContainer {
      max-width: unset;
      text-align: center;
      padding-left: 0;
    }

    .TitleWithBackground__titleImage {
      width: 255px;
      height: 62px;
      left: calc(var(--intro-image-x-position) + 92px);
    }

    .TitleWithBackground__description {
      text-align: center;
      margin-top: 40px;
      margin-left: unset;
      max-width: unset;
      padding-right: 0;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .TitleWithBackground {
      padding-top: 96px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .TitleWithBackground {
      padding-top: 64px;
      padding-bottom: 64px;
      text-align: unset;
      min-width: unset;
    }

    .TitleWithBackground__titleContainer {
      text-align: unset;
      min-width: unset;
    }

    .TitleWithBackground__titleImage {
      width: 243px;
    }

    .TitleWithBackground__description {
      text-align: unset;
      margin-top: 36px;
    }
  }
`;

gql`
  fragment Introduction1TitleContent on ComponentNestedIntroduction1Title {
    title
    description
    textBackground {
      data {
        textBackground: attributes {
          src: hash
          alt: alternativeText
          ext
        }
      }
    }
  }
`;

export { TitleWithBackground };
